import React, { Fragment } from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Typography from "@mui/material/Typography";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import { useTheme } from "@mui/material/styles";
import {
  PUSH_SELECT_ALL_COUNTRIES,
  pushUserTargetTypes,
} from "./notificationConstant";
import {
  getPushCountryCodes,
  normalizePushCountrySelection,
} from "./notificationFormUtils";
import { getAllowedCountrySelectOptions } from "../../utils/general";
import { selectMenuProps } from "../../utils/selectMenuProps";
import { useDesignTokens } from "../../theme/useDesignTokens";
import FormInput from "../Common/FormInputs/MuiTextField";
import {
  emailCompactFormControlSx,
  emailFieldRowSx,
  emailFocusOutlineSx,
  emailSectionCardSx,
} from "./emailFormStyles";

const UserSelection = ({
  userTargetType = pushUserTargetTypes.broadcast.value,
  handleUserTargetTypeChange,
  countries = [],
  handleUserCountries,
  allowedCountries = [],
  userIdCSVFileInputRef,
  handleUserIdFileInput,
  uploadedUserIdFileInput = "",
}) => {
  const theme = useTheme();
  const { colors } = useDesignTokens();
  const primaryColor = colors?.primary?.main || theme.palette.primary.main;
  const focusOutlineSx = emailFocusOutlineSx(primaryColor);
  const controlSx = emailCompactFormControlSx(theme);
  const countryOptions = getAllowedCountrySelectOptions(allowedCountries);
  const allCountryCodes = getPushCountryCodes(allowedCountries);
  const selectedCountries = Array.isArray(countries) ? countries : [];
  const allSelected =
    allCountryCodes.length > 0 &&
    allCountryCodes.every((code) => selectedCountries.includes(code));
  const isBroadcast = userTargetType === pushUserTargetTypes.broadcast.value;
  const isUserSpecific =
    userTargetType === pushUserTargetTypes.userSpecific.value;

  const handleCountryChange = (event) => {
    handleUserCountries(
      normalizePushCountrySelection(event.target.value, allCountryCodes)
    );
  };

  return (
    <Fragment>
      <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 1 }}>
        Users
      </Typography>
      <Paper sx={emailSectionCardSx}>
        <Box
          sx={{
            ...emailFieldRowSx,
            alignItems: { xs: "flex-start", md: "center" },
          }}
        >
          <Typography variant="body2" sx={{ fontWeight: 400 }}>
            Target Type
            <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
              *
            </Typography>
          </Typography>
          <Box sx={{ minWidth: 0 }}>
            <RadioGroup
              row
              value={userTargetType}
              onChange={(e) => handleUserTargetTypeChange(e.target.value)}
              sx={{ gap: { xs: 0.5, sm: 2 }, flexWrap: "wrap" }}
            >
              <FormControlLabel
                value={pushUserTargetTypes.broadcast.value}
                control={<Radio size="small" color="primary" />}
                label={pushUserTargetTypes.broadcast.label}
              />
              <FormControlLabel
                value={pushUserTargetTypes.userSpecific.value}
                control={<Radio size="small" color="primary" />}
                label={pushUserTargetTypes.userSpecific.label}
              />
            </RadioGroup>
          </Box>
        </Box>

        {isBroadcast && (
          <Box sx={emailFieldRowSx}>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              Country
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            </Typography>
            <Box sx={controlSx}>
              <FormInput
                id="select-outlined-country-name-id"
                type="select"
                multiple
                size="small"
                fullWidth
                name="country"
                value={selectedCountries}
                onChange={handleCountryChange}
                displayEmpty
                renderValue={(selected) => {
                  const values = Array.isArray(selected) ? selected : [];
                  if (!values.length) return "Select Country";
                  if (
                    allCountryCodes.length > 0 &&
                    allCountryCodes.every((code) => values.includes(code))
                  ) {
                    return "All";
                  }
                  return values.join(", ");
                }}
                MenuProps={selectMenuProps}
                options={[
                  { value: PUSH_SELECT_ALL_COUNTRIES, label: "Select All" },
                  ...countryOptions,
                ]}
                renderOption={(option) => {
                  const isSelectAll = option.value === PUSH_SELECT_ALL_COUNTRIES;
                  const checked = isSelectAll
                    ? allSelected
                    : selectedCountries.includes(option.value);
                  return (
                    <MenuItem key={option.value} value={option.value} dense>
                      <Checkbox checked={checked} size="small" sx={{ p: 0.5, mr: 1 }} />
                      <ListItemText primary={option.label} />
                    </MenuItem>
                  );
                }}
                sx={focusOutlineSx}
              />
            </Box>
          </Box>
        )}

        {isUserSpecific && (
          <Box sx={emailFieldRowSx}>
            <Typography variant="body2" sx={{ fontWeight: 400 }}>
              User IDs CSV
              <Typography component="span" color="error.main" sx={{ ml: 0.25 }}>
                *
              </Typography>
            </Typography>
            <Box sx={controlSx}>
              <FormInput
                type="file"
                size="small"
                fullWidth
                accept=".csv"
                name={uploadedUserIdFileInput?.name ?? uploadedUserIdFileInput}
                inputRef={userIdCSVFileInputRef}
                onChange={(e) => handleUserIdFileInput(e.target.files?.[0])}
                sx={focusOutlineSx}
              />
            </Box>
          </Box>
        )}
      </Paper>
    </Fragment>
  );
};

export default UserSelection;
